import {
	MyPayments,
	MyReferralsWrapper as MyReferralsWrapperOld,
	ReferralsFlow as ReferralsFlowOld,
	ReferralsList as ReferralsListOld,
	RefundWrapper as RefundWrapperOld,
} from 'presentation/pages';
import type {RouteObject} from 'react-router-dom';
import {Navigate} from 'react-router-dom';
import React, {Suspense} from 'react';
import {
	AuthorizationsEnum,
	ChangePlanEnum,
	ChangeDueDateEnum,
	ContractAdaptationEnum,
	ContractReactivationEnum,
	FinancialPendencyEnum,
	MyPaymentsEnum,
	MyReferralsEnum,
	RefundEnum,
} from './mapRoutesFinancial';
import {translations} from '../translations';
import type {FinancialArchitectureFlags} from './types';
import {Loading} from 'presentation/components/Loading/Loading';
import {FinancialPendency as FinancialPendencyOld} from 'presentation/pages/private/financial-pendency';
import {
	MakeAuthorizations as MakeAuthorizationsOld,
	MakeCitySelection as MakeCitySelectionOld,
	MakeUfSelection as MakeUfSelectionOld,
	MakeAuthorizationsList as MakeAuthorizationsListOld,
	MakeAuthorizationsRequestsList as MakeAuthorizationsRequestsListOld,
} from 'main/factories/pages';
import {
	Authorizations as AuthorizationsOld,
	NewRequestInstructions as NewRequestInstructionsOld,
	RequestAuthorization as RequestAuthorizationOld,
	RequestDetail as RequestDetailOld,
} from 'presentation/pages/private/authorizations/pages';
import {RequestStates as RequestStatesOld} from 'presentation/pages/private/authorizations/pages/RequestAuthorization/pages/RequestStates';
import {RequestCities as RequestCitiesOld} from 'presentation/pages/private/authorizations/pages/RequestAuthorization/pages/RequestCities';
import {AuthorizationsOdontoWrapper as AuthorizationsOdontoWrapperOld} from 'presentation/pages/private/authorizations/AuthorizationsOdontoWrapper';
import {AuthorizationsOdonto as AuthorizationsOdontoOld} from 'presentation/pages/private/authorizations/pages/AuthorizationsOdontoFlow';
import {
	ConsultRefundEntry as ConsultRefundEntryOld,
	ConsultRefundPreview as ConsultRefundPreviewOld,
	RefundList as RefundListOld,
	Request as RequestOld,
	RequestPreview as RequestPreviewOld,
	SendAdditionalDocs as SendAdditionalDocsOld,
} from 'presentation/pages/private/refund/pages';
import {TermsPage as TermsPageOld} from 'presentation/pages/private/refund/components';
import {CHANGE_PLAN_LOCATION_FLOW} from 'presentation/utils';

const {loading} = translations['pt-br'].commons;

const MakeOpenBillsOld = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeOpenBills})),
);
const MakePaymentsHistoryOld = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakePaymentsHistory})),
);
const MakeBilletDetailsOld = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakeBilletDetails})),
);
const MakePaymentsIncomeTaxOld = React.lazy(() =>
	import('main/factories/pages').then(m => ({default: m.MakePaymentsIncomeTax})),
);

const IncomeTax = React.lazy(() =>
	import('../pages/income-tax/pages/IncomeTaxPage').then(m => ({default: m.IncomeTaxPage})),
);

const MyReferralsWrapperPage = React.lazy(() =>
	import('../pages/my-referrals/MyReferralsWrapper').then(m => ({
		default: m.MyReferralsWrapper,
	})),
);

const MyReferralsViewPage = React.lazy(() =>
	import('../pages/my-referrals/views/MyReferralsView').then(m => ({
		default: m.MyReferralsView,
	})),
);

const ReferralsListViewPage = React.lazy(() =>
	import('../pages/my-referrals/views/ReferralsListView').then(m => ({
		default: m.ReferralsListView,
	})),
);

const CoparticipationExtractV2 = React.lazy(() =>
	import('../pages/coparticipation-extract-v2/pages/CoparticipationPage').then(m => ({
		default: m.CoparticipationPage,
	})),
);

const CoparticipationExtract = React.lazy(() =>
	import('../pages/coparticipation-extract/pages/CoparticipationPage').then(m => ({
		default: m.CoparticipationPage,
	})),
);

const ContractAdaptationPage = React.lazy(() =>
	import('../pages/contract-adaptation/pages/ContractAdaptationPage').then(m => ({
		default: m.ContractAdaptationPage,
	})),
);

const ContractReactivationPage = React.lazy(() =>
	import('../pages/contract-reactivation/pages/ContractReactivationPage').then(m => ({
		default: m.ContractReactivationPage,
	})),
);

const ChangePlanPage = React.lazy(() =>
	import('../pages/change-plan/pages/ChangePlanPage').then(m => ({
		default: m.ChangePlanPage,
	})),
);

const ChangePlanNewPlanFlowPage = React.lazy(() =>
	import('../pages/change-plan/pages/ChangePlanNewPlanFlowPage').then(m => ({
		default: m.ChangePlanNewPlanFlowPage,
	})),
);

const ChangeDueDatePage = React.lazy(() =>
	import('../pages/change-due-date/pages/ChangeDueDatePage').then(m => ({
		default: m.ChangeDueDatePage,
	})),
);

const buildFinancialRoutes = ({
	isEnabledCopartV3,
	isEnabledNewIncomeTax,
	isEnabledNewReferrals,
}: FinancialArchitectureFlags) => {
	const financialRoutes: RouteObject[] = [
		{
			element: <MyPayments />,
			children: [
				{
					path: MyPaymentsEnum.OPEN_BILLS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MakeOpenBillsOld />
						</Suspense>
					),
				},
				{
					path: MyPaymentsEnum.PAYMENTS_HISTORY,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MakePaymentsHistoryOld />
						</Suspense>
					),
				},
				{
					path: MyPaymentsEnum.BILLET_DETAILS,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MakeBilletDetailsOld />
						</Suspense>
					),
				},
				{
					path: MyPaymentsEnum.INCOME_TAX,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							{isEnabledNewIncomeTax ? <IncomeTax /> : <MakePaymentsIncomeTaxOld />}
						</Suspense>
					),
				},
				{
					path: MyPaymentsEnum.COPARTICIPATION,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							{isEnabledCopartV3 ? (
								<CoparticipationExtract />
							) : (
								<CoparticipationExtractV2 />
							)}
						</Suspense>
					),
				},
			],
		},
		{
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					{isEnabledNewReferrals ? <MyReferralsWrapperPage /> : <MyReferralsWrapperOld />}
				</Suspense>
			),
			children: [
				{
					element: isEnabledNewReferrals ? (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<MyReferralsViewPage />
						</Suspense>
					) : (
						<ReferralsFlowOld />
					),
					children: [
						{
							path: MyReferralsEnum.MY_REFERRALS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									{isEnabledNewReferrals ? (
										<ReferralsListViewPage />
									) : (
										<ReferralsListOld />
									)}
								</Suspense>
							),
						},
						{
							path: MyReferralsEnum.MY_REFERRALS_HISTORY,
							element: <Navigate to={MyReferralsEnum.MY_REFERRALS} replace />,
						},
					],
				},
			],
		},
		{
			path: FinancialPendencyEnum.FINANCIAL_PENDENCY,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<FinancialPendencyOld />
				</Suspense>
			),
		},
		{
			path: ContractAdaptationEnum.CONTRACT_ADAPTATION,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<ContractAdaptationPage />
				</Suspense>
			),
		},
		{
			path: ContractReactivationEnum.CONTRACT_REACTIVATION,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<ContractReactivationPage />
				</Suspense>
			),
		},
		{
			path: ChangePlanEnum.CHANGE_PLAN_NEW_PLAN,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<ChangePlanNewPlanFlowPage />
				</Suspense>
			),
		},
		{
			path: ChangeDueDateEnum.CHANGE_DUE_DATE,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<ChangeDueDatePage />
				</Suspense>
			),
		},
		{
			path: ChangePlanEnum.CHANGE_PLAN_NEW_PLAN_UF,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<MakeUfSelectionOld flowType={CHANGE_PLAN_LOCATION_FLOW} />
				</Suspense>
			),
		},
		{
			path: ChangePlanEnum.CHANGE_PLAN_NEW_PLAN_CITY,
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<MakeCitySelectionOld flowType={CHANGE_PLAN_LOCATION_FLOW} />
				</Suspense>
			),
		},
		{
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<ChangePlanPage />
				</Suspense>
			),
			children: [
				{
					path: ChangePlanEnum.CHANGE_PLAN,
				},
				{
					path: ChangePlanEnum.CHANGE_PLAN_HISTORY,
				},
			],
		},
		{
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<MakeAuthorizationsOld />
				</Suspense>
			),
			children: [
				{
					element: <AuthorizationsOld />,
					children: [
						{
							path: AuthorizationsEnum.AUTHORIZATIONS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeAuthorizationsListOld />
								</Suspense>
							),
						},
						{
							path: AuthorizationsEnum.AUTHORIZATIONS_REQUESTS,
							element: (
								<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
									<MakeAuthorizationsRequestsListOld />
								</Suspense>
							),
						},
					],
				},
				{
					element: <NewRequestInstructionsOld />,
					path: AuthorizationsEnum.INSTRUCTIONS,
				},
				{
					path: AuthorizationsEnum.REQUEST_INITIAL_FLOW_UF,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<RequestStatesOld />
						</Suspense>
					),
				},
				{
					path: AuthorizationsEnum.REQUEST_INITIAL_FLOW_CITY,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<RequestCitiesOld />
						</Suspense>
					),
				},
				{
					element: <RequestAuthorizationOld />,
					path: AuthorizationsEnum.REQUEST,
				},
				{
					element: <RequestDetailOld />,
					path: AuthorizationsEnum.REQUEST_DETAIL,
				},
			],
		},
		{
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<AuthorizationsOdontoWrapperOld />
				</Suspense>
			),
			children: [
				{
					path: AuthorizationsEnum.AUTHORIZATIONS_ODONTO,
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<AuthorizationsOdontoOld />
						</Suspense>
					),
				},
			],
		},
		{
			element: (
				<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
					<RefundWrapperOld />
				</Suspense>
			),
			children: [
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<RefundListOld />
						</Suspense>
					),
					path: RefundEnum.REFUND,
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<RequestOld />
						</Suspense>
					),
					path: RefundEnum.REQUEST,
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<RequestPreviewOld />
						</Suspense>
					),
					path: RefundEnum.REQUEST_PREVIEW,
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<ConsultRefundEntryOld />
						</Suspense>
					),
					path: RefundEnum.CONSULT,
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<ConsultRefundPreviewOld />
						</Suspense>
					),
					path: RefundEnum.CONSULT_PREVIEW,
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<SendAdditionalDocsOld />
						</Suspense>
					),
					path: RefundEnum.SENDING_DOCUMENTS,
				},
				{
					element: (
						<Suspense fallback={<Loading text={loading} minHeight="100%" />}>
							<TermsPageOld />
						</Suspense>
					),
					path: RefundEnum.TERMS_DEFAULT,
				},
			],
		},
	];

	return financialRoutes;
};

export const getFinancialRoutes = buildFinancialRoutes;
